import { Close, Save } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  IconButton,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import theme from "../../theme/theme";
import QuotationV2Fields, {
  QuotationTemplateSelect,
  QuotationSensorFields,
} from "./QuotationV2Fields";

const NewQuotationDialog = ({
  open,
  onClose,
  loading,
  saving,
  quotation,
  setQuotation,
  onSave,
  errors,
}) => {
  const handleChange = (key) => (e) =>
    setQuotation({ ...quotation, [key]: e.target.value });

  const getError = (key) => !!errors[key];
  const getHelperText = (key) =>
    Array.isArray(errors[key]) ? errors[key][0] : errors[key];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.dialog.borderRadius,
          minWidth: theme.dialog.minWidth,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: theme.dialog.headerBgColor,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          color={theme.palette.primary.main}
        >
          New Quotation
        </Typography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ p: 4 }}>
        {loading ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            {[...Array(26)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={40} />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="Quotation No."
              id="generated_id"
              fullWidth
              size="small"
              value={quotation.generated_id || ""}
              onChange={handleChange("generated_id")}
              error={getError("generated_id")}
              helperText={
                getHelperText("generated_id") ||
                "Auto-generated for this financial year — edit if required."
              }
              sx={{ gridColumn: "1 / -1" }}
            />
            <QuotationTemplateSelect
              quotation={quotation}
              setQuotation={setQuotation}
            />
            <TextField
              label="Type of Lift"
              id="type_of_lift"
              fullWidth
              size="small"
              value={quotation.type_of_lift || ""}
              onChange={handleChange("type_of_lift")}
              error={getError("type_of_lift")}
              helperText={getHelperText("type_of_lift")}
            />
            <TextField
              label="Capacity"
              id="capacity"
              fullWidth
              size="small"
              value={quotation.capacity || ""}
              onChange={handleChange("capacity")}
              error={getError("capacity")}
              helperText={getHelperText("capacity")}
            />
            <TextField
              label="Speed"
              id="speed"
              fullWidth
              size="small"
              value={quotation.speed || ""}
              onChange={handleChange("speed")}
              error={getError("speed")}
              helperText={getHelperText("speed")}
            />
            <TextField
              label="Floor"
              id="floor"
              fullWidth
              size="small"
              value={quotation.floor || ""}
              onChange={handleChange("floor")}
              error={getError("floor")}
              helperText={getHelperText("floor")}
            />
            <TextField
              label="Landings & Openings"
              id="landings_openings"
              fullWidth
              size="small"
              value={quotation.landings_openings || ""}
              onChange={handleChange("landings_openings")}
              error={getError("landings_openings")}
              helperText={getHelperText("landings_openings")}
            />
            <TextField
              label="Cabin Construction"
              id="cabin_construction"
              fullWidth
              size="small"
              value={quotation.cabin_construction || ""}
              onChange={handleChange("cabin_construction")}
              error={getError("cabin_construction")}
              helperText={getHelperText("cabin_construction")}
            />
            <TextField
              label="Car Inside"
              id="car_inside"
              fullWidth
              size="small"
              value={quotation.car_inside || ""}
              onChange={handleChange("car_inside")}
              error={getError("car_inside")}
              helperText={getHelperText("car_inside")}
            />
            <TextField
              label="Car Door Header"
              id="car_door_header"
              fullWidth
              size="small"
              value={quotation.car_door_header || ""}
              onChange={handleChange("car_door_header")}
              error={getError("car_door_header")}
              helperText={getHelperText("car_door_header")}
            />
            <TextField
              label="Landing Door Header"
              id="landing_door_header"
              fullWidth
              size="small"
              value={quotation.landing_door_header || ""}
              onChange={handleChange("landing_door_header")}
              error={getError("landing_door_header")}
              helperText={getHelperText("landing_door_header")}
            />
            <TextField
              label="Clear Opening"
              id="clear_opening"
              fullWidth
              size="small"
              value={quotation.clear_opening || ""}
              onChange={handleChange("clear_opening")}
              error={getError("clear_opening")}
              helperText={getHelperText("clear_opening")}
            />
            <TextField
              label="Warranty"
              id="warranty"
              fullWidth
              size="small"
              value={quotation.warranty || ""}
              onChange={handleChange("warranty")}
              error={getError("warranty")}
              helperText={getHelperText("warranty")}
            />
            <TextField
              label="Machine"
              id="machine"
              fullWidth
              size="small"
              value={quotation.machine || ""}
              onChange={handleChange("machine")}
              error={getError("machine")}
              helperText={getHelperText("machine")}
            />
            <TextField
              label="Guide Rails"
              id="guide_rails"
              fullWidth
              size="small"
              value={quotation.guide_rails || ""}
              onChange={handleChange("guide_rails")}
              error={getError("guide_rails")}
              helperText={getHelperText("guide_rails")}
            />
            <TextField
              label="Landing / Car Door Mechanism"
              id="car_door_mechanism"
              fullWidth
              size="small"
              value={quotation.car_door_mechanism || ""}
              onChange={handleChange("car_door_mechanism")}
              error={getError("car_door_mechanism")}
              helperText={getHelperText("car_door_mechanism")}
            />
            <TextField
              label="Door Frame"
              id="doors"
              fullWidth
              size="small"
              value={quotation.doors || ""}
              onChange={handleChange("doors")}
              error={getError("doors")}
              helperText={getHelperText("doors")}
            />
            <TextField
              label="Rope"
              id="rope"
              fullWidth
              size="small"
              value={quotation.rope || ""}
              onChange={handleChange("rope")}
              error={getError("rope")}
              helperText={getHelperText("rope")}
            />
            <TextField
              label="Car Frame"
              id="car_frame"
              fullWidth
              size="small"
              value={quotation.car_frame || ""}
              onChange={handleChange("car_frame")}
              error={getError("car_frame")}
              helperText={getHelperText("car_frame")}
            />
            <TextField
              label="Cabin"
              id="cabin"
              fullWidth
              size="small"
              value={quotation.cabin || ""}
              onChange={handleChange("cabin")}
              error={getError("cabin")}
              helperText={getHelperText("cabin")}
            />
            <TextField
              label="Controller"
              id="controller"
              fullWidth
              size="small"
              value={quotation.controller || ""}
              onChange={handleChange("controller")}
              error={getError("controller")}
              helperText={getHelperText("controller")}
            />
            <TextField
              label="Buttons"
              id="buttons"
              fullWidth
              size="small"
              value={quotation.buttons || ""}
              onChange={handleChange("buttons")}
              error={getError("buttons")}
              helperText={getHelperText("buttons")}
            />
            <TextField
              label="Wiring"
              id="wiring"
              fullWidth
              size="small"
              value={quotation.wiring || ""}
              onChange={handleChange("wiring")}
              error={getError("wiring")}
              helperText={getHelperText("wiring")}
            />
            <TextField
              label="Travelling Cable"
              id="travelling_cable"
              fullWidth
              size="small"
              value={quotation.travelling_cable || ""}
              onChange={handleChange("travelling_cable")}
              error={getError("travelling_cable")}
              helperText={getHelperText("travelling_cable")}
            />
            <TextField
              label="OSG"
              id="osg"
              fullWidth
              size="small"
              value={quotation.osg || ""}
              onChange={handleChange("osg")}
              error={getError("osg")}
              helperText={getHelperText("osg")}
            />
            <QuotationSensorFields
              quotation={quotation}
              setQuotation={setQuotation}
            />
            <TextField
              label="Price"
              id="price"
              fullWidth
              size="small"
              value={quotation.price || ""}
              onChange={handleChange("price")}
              error={getError("price")}
              helperText={getHelperText("price")}
            />
            <TextField
              label="GST Price"
              id="gst_price"
              fullWidth
              size="small"
              value={quotation.gst_price || ""}
              onChange={handleChange("gst_price")}
              error={getError("gst_price")}
              helperText={getHelperText("gst_price")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!quotation.is_free_maintenance}
                  onChange={(e) =>
                    setQuotation({
                      ...quotation,
                      is_free_maintenance: e.target.checked,
                    })
                  }
                />
              }
              label="Free Maintenance"
              sx={{ gridColumn: "1 / -1" }}
            />
            <QuotationV2Fields
              quotation={quotation}
              setQuotation={setQuotation}
              errors={errors}
            />
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          backgroundColor: theme.dialog.headerBgColor,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            backgroundColor: theme.button.backgroundColor,
            color: "#000",
            borderRadius: theme.button.borderRadius,
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          loading={saving}
          startIcon={<Save />}
          sx={{
            backgroundColor: theme.button.editBackgroundColor,
            borderRadius: theme.button.borderRadius,
          }}
        >
          Save Quotation
        </Button>
      </Box>
    </Dialog>
  );
};

export default NewQuotationDialog;
